import { WalletError, type WalletErrorCode } from '@cef-ai/wallet-api-client';

/**
 * User-facing copy for a `WalletError`, selected by `.code`.
 *
 * ONE RULE governs every entry, and it is the reason this comment exists:
 * **a message may only name an action that exists in this build and is
 * reachable by the person who is reading it.** A sentence that sends a
 * locked-out user to a screen with nothing on it is worse than no advice at
 * all — it costs them the trip, and it hides the fact that they are stuck.
 * Where the answer is "nothing you can do here", say that plainly.
 *
 * WHO READS THIS. `walletErrorMessage` is rendered in FOUR places:
 * `routes/Login`, `routes/Register`, the embedded registration prompt
 * (`popup/components/RegisterPrompt`, since Task 22), and — as the `default`
 * fallback of `Settings/emailBindErrorMessage`, itself rendered inside
 * `EmailBindFlow` on the Settings page — `routes/Settings`. The first three
 * go through `registerErrorMessage`, which overrides the registration-specific
 * codes first; the fourth goes through `emailBindErrorMessage`'s own table
 * and only reaches this copy for a code that table does not special-case.
 *
 * The first three are PRE-AUTH screens — there is no signed-in session, no
 * nav, and no "Sign out" button (that lives in `AppPage`). The FOURTH IS
 * DIFFERENT: it renders inside `Settings`, which is wrapped in `AppPage`, so
 * a signed-in session, nav, and "Sign out" all exist on that screen. Any
 * entry this table adds must stay true for a reader who already has all of
 * that available — "sign out and back in" is still not the fix for anything
 * here (nothing below asks for it), but it is no longer unconditionally
 * unreachable the way it is for the other three. Errors reaching a HOST app
 * travel as `wallet:error` carrying `err.detail`, not this copy, so entries
 * for host-facing codes are never shown to an end user by us.
 *
 * The popup prompt was deliberately built to offer the SAME set of actions
 * `/register` does — send a code, resend it, use a different address, retry
 * the ceremony, sign in instead — precisely so that adding a reader did not
 * turn any of these sentences into a phantom. Adding an action to one of
 * those two screens without the other is what would.
 *
 * WHAT DOES NOT EXIST YET, and must therefore never be named: adding a second
 * device to an account (recover-then-enrol — `accounts.putEnvelope` has zero
 * callers anywhere in `src` or `packages/identity`), password/recovery-based
 * unlock, and any "forgot device" flow. Settings renders exactly three things:
 * Credential, Diagnostics, and Export account.
 */
const FRIENDLY: Partial<Record<WalletErrorCode, string>> = {
  'user-cancelled': "Cancelled. You can try again whenever you're ready.",
  unauthorized: 'Please sign in to continue.',
  'token-expired': 'Your session expired. Please sign in again.',
  'refresh-invalid': 'Your session could not be renewed. Please sign in again.',
  'popup-blocked': 'Your browser blocked the wallet popup. Allow popups for this site and try again.',
  'popup-closed': "The wallet window closed before you finished. Try again when you're ready.",
  'prf-unsupported':
    'That passkey can\'t generate the key SCP Wallet needs — password-manager passkeys (Bitwarden, 1Password, etc.) often can\'t. In the passkey prompt, choose "This device" (Touch ID / Windows Hello), your phone, or a security key, then try again.',
  'origin-mismatch': "This page isn't allowed to talk to the wallet.",
  // Was "Please sign out and back in" — but this only ever renders on a
  // pre-auth screen, where there is nothing to sign out of.
  'derivation-mismatch':
    "The wallet's keys don't match what the server expects, so we stopped rather than open the wrong wallet. Try signing in again.",
  'credential-not-found': "We couldn't find your passkey on this device. Try signing in with the device that has it.",
  'credential-already-exists': 'A wallet already exists for this passkey on this device. Sign in instead.',
  'attestation-invalid': "Your device couldn't prove the passkey is valid. Please try again.",
  'assertion-invalid': "We couldn't verify your passkey. Please try again.",
  'challenge-expired': 'That took a little too long — please try again.',
  'challenge-not-found': 'Something went wrong starting that request. Please try again.',
  'signcount-replay': 'Your passkey may be in use elsewhere. Please sign in again.',
  // Same fix as `derivation-mismatch`: no sign-out exists where this is shown.
  'address-mismatch':
    "The wallet address didn't match what we expected, so we stopped rather than continue. Try signing in again.",
  'scope-not-allowed': "This app asked for permissions you don't have.",
  // Was "Please ask the app to shorten it" — the user has no way to do that,
  // so the honest version says there is nothing for them to do.
  'ttl-too-long':
    'This app asked for a permission that would last longer than the wallet allows. There is nothing to change here — the app has to ask for a shorter one.',
  // Nothing the reader can do: the app opened the wrong wallet surface for a
  // registration. Same shape as `ttl-too-long` above — say plainly that the
  // fix is not theirs, rather than inventing an action for them.
  'registration-unsupported':
    'This window can’t set up a new wallet. There is nothing to change here — the app has to open the wallet’s own window to create one.',
  // Reachable, so this one DOES name an action, and both halves of it exist
  // wherever this is shown: the flow is in an open window the reader can
  // return to, and closing a window is always available.
  'registration-in-progress':
    'Your wallet setup is already open in the wallet window. Finish it there, or close that window and start again.',
  'email-unverified': 'Please confirm your email address before creating your wallet.',
  'email-conflict': 'That email address is already in use. Sign in instead.',
  'otp-invalid': "That code didn't match. Check the digits, or request a new one.",
  'rate-limited': 'Too many attempts. Wait a few minutes, then try again.',
  validation: "That didn't look right. Check what you entered and try again.",
  // Was "Sign in on a device you already use, then add this one from
  // Settings." Adding a device does not exist in this build, and Settings has
  // no such screen — this message reaches someone locked out on a NEW device,
  // i.e. exactly the person that advice would strand.
  'root-envelope-missing':
    "This passkey isn't set up to open your wallet, and adding a new device isn't available in this version yet. Sign in with the passkey you created the wallet with.",
  // Was "...and update the app if the problem continues." Two independent
  // reviews found that dead copy: nothing shows this screen ever renders
  // inside a native shell, and the only reader is a browser user, who has no
  // app to update. The next attempt ("...contact support") was ALSO a
  // phantom affordance — a third recurrence of the same class this file's
  // header rule exists to catch: grepping `support@|contact support|mailto:`
  // across this build turns up nothing, so there is no support channel to
  // send anyone to. Reworded to name only something a browser user can
  // actually do here and now: reload, or fall back to the one passkey that
  // never needs an envelope at all (see `root-envelope-missing` above).
  'root-wrap-version-unknown':
    "We couldn't unlock your wallet on this device. Reload the page, or sign in with the passkey you created the wallet with.",
  // Distinct from `root-envelope-missing` on purpose. That one means "this
  // credential really is not set up to open this wallet"; this one means the
  // session response never carried `isOrigin`, so we could not tell which
  // kind of credential it is. Blaming the passkey there would be a lie, and
  // the only thing the reader can do about a wire mismatch is reload.
  'session-response-malformed':
    "We couldn't read the wallet service's reply, so we stopped rather than risk opening the wrong wallet. Reload the page and try again.",
  'root-wrap-source-unsupported':
    "This device is set up to be unlocked with a recovery password, not with this passkey — and password recovery isn't available yet. Sign in with the passkey this device was set up with, or on a device you already use.",
  // Was "...or recover your wallet from another device." There is no recovery
  // flow to send anyone to.
  'root-unwrap-failed':
    "We couldn't unlock your wallet with this passkey. Sign in with the passkey you created this wallet with — there is no recovery flow in this version yet.",
  internal: 'Something went wrong on our side. Please try again in a moment.',
};

export function walletErrorMessage(e: unknown): string {
  if (e instanceof WalletError) {
    // The fallback is deliberate but imperfect: `e.detail` is SERVER text, not
    // copy anyone has vetted against the rule above. Prefer adding an entry to
    // FRIENDLY over relying on it.
    return FRIENDLY[e.code] ?? e.detail ?? e.message;
  }
  if (e instanceof Error) {
    return e.message;
  }
  return 'Something unexpected happened. Please try again.';
}
